import { pluralizeCopy } from "./copy";
import get from "lodash/get";
import pick from "lodash/pick";
import merge from "lodash/merge";
import compact from "lodash/compact";
import omit from "lodash/omit";
import cloneDeep from "lodash/cloneDeep";
import set from "lodash/set";
import shuffle from "lodash/shuffle";
import sample from "lodash/sample";
import {
  CONTACTLESS_PREVIEW_MODE,
  DEFAULT_CONTACTLESS_IMG
} from "constants/contactless";
import { extendQS } from "./route";
import { PLATFORM_SOURCE } from "./constants/payment";
import { FORM_KEY } from "utils/constants/form";
import { FILE_EXTENSIONS } from "utils/constants/attachment";
import { BLUE_SECONDARY, BLUE_PRIMARY } from "utils/styles";
import { STATE_KEYS } from "./constants/state";
import { getConfig } from "./env";
import { API_PATHS } from "./paths";
import { USER } from "./constants/models";

const WHITE_COLOR = "#ffffff";

const DOTS_OPTIONS_TYPES = [
  "rounded",
  "dots",
  "classy",
  "classy-rounded",
  "square",
  "extra-rounded"
];
const CORNER_SQUARE_OPTIONS_TYPES = ["dot", "square", "extra-rounded"];
const CORNERS_DOT_OPTIONS_TYPES = ["dot", "square"];

export const getDowloadTitleFromCheckout = (checkout) => {
  const lineItems = get(checkout, "lineItems", []);
  const lineItemsCount = lineItems.length;
  let productLabel = "";
  if (lineItemsCount > 0) {
    productLabel = lineItems[0].label && lineItems[0].label.trim();
    if (lineItemsCount > 1) {
      productLabel += `+ ${pluralizeCopy("other", lineItemsCount - 1)}`;
    }
  }
  let label = get(checkout, "banner.label", "");
  const sublabel = get(checkout, "banner.sublabel", "");
  if (sublabel && label) {
    label = `${label} - ${sublabel}`;
  }
  if (label) {
    label = label.trim();
  }

  if (productLabel) {
    return compact([label, productLabel]).join(" ");
  } else {
    return label;
  }
};

export const getDefaultQRCodeStyles = ({
  url,
  primaryColor = BLUE_SECONDARY,
  secondaryColor = BLUE_PRIMARY,
  image = DEFAULT_CONTACTLESS_IMG
} = {}) => ({
  margin: 0,
  url,
  image,
  qrOptions: {
    typeNumber: 0,
    mode: "Byte",
    errorCorrectionLevel: "H"
  },
  imageOptions: {
    hideBackgroundDots: true,
    imageSize: 0.5,
    margin: 0,
    crossOrigin: "anonymous"
    // Excavate
  },
  dotsOptions: {
    color: primaryColor,
    //   gradient: {},
    // 'rounded' 'dots' 'classy' 'classy-rounded' 'square' 'extra-rounded'
    type: "rounded"
  },
  cornersSquareOptions: {
    color: primaryColor,
    //   gradient: {},
    // 'dot' 'square' 'extra-rounded'
    type: "dot"
  },
  cornersDotOptions: {
    color: secondaryColor,
    //   gradient: {},
    // 'dot' 'square'
    type: "dot"
  },
  backgroundOptions: {
    color: "#ffffff"
    //   gradient: {}
  }
});

const INITIAL_FORM_VALUES = {
  resource: USER,
  name: "",
  rootUrl: getConfig("WEB_CLIENT_ROOT"),
  testMode: false,
  previewMode: CONTACTLESS_PREVIEW_MODE.CODE,
  contactless: true,
  codeSizeFactor: 1,
  downloadSize: 1200,
  widthFactor: 0.33,
  heightFactor: 0.75,
  extension: FILE_EXTENSIONS.JPEG,
  type: "canvas"
};

export const getContactlessConfigInitialValues = ({
  formValues,
  merchantAccount,
  url,
  contactlessConfigData
}) => {
  let businessIcon = get(
    merchantAccount,
    `merchant_account_config.${STATE_KEYS.MERCHANT_ACCOUNT_CONFIG.BRANDING_ICON}.src`
  );
  if (!businessIcon) {
    businessIcon = get(merchantAccount, "profile.businessIcon");
  }
  let businessLogo = get(
    merchantAccount,
    `merchant_account_config.${STATE_KEYS.MERCHANT_ACCOUNT_CONFIG.BRANDING_LOGO}.src`
  );
  if (!businessLogo) {
    businessLogo = get(merchantAccount, "profile.businessLogo");
  }

  let primaryColor = get(merchantAccount, "profile.primaryColor");
  if (!primaryColor) {
    primaryColor = BLUE_SECONDARY;
  }

  let secondaryColor = get(merchantAccount, "profile.secondaryColor");
  if (!secondaryColor) {
    secondaryColor = BLUE_PRIMARY;
  }

  if (primaryColor === WHITE_COLOR) {
    primaryColor =
      secondaryColor !== WHITE_COLOR ? secondaryColor : BLUE_SECONDARY;
  }
  const image = businessIcon || businessLogo || DEFAULT_CONTACTLESS_IMG;

  const defaultValues = getDefaultQRCodeStyles({
    url,
    image,
    primaryColor,
    secondaryColor
  });
  const mergedValues = merge(
    defaultValues,
    omit(contactlessConfigData, ["image"])
  );

  const initialValues = {
    [FORM_KEY]: {
      ...cloneDeep(INITIAL_FORM_VALUES),
      ...formValues
    },
    ...mergedValues
  };

  return initialValues;
};

export const getManualContactlessConfigInitialValues = ({
  url = getConfig("WEB_CLIENT_ROOT"),
  withRedirect = false,
  manual = true,
  qrCode = {}
}) => {
  const defaultValues = getDefaultQRCodeStyles({
    url
  });

  const initialValues = {
    [FORM_KEY]: {
      ...cloneDeep(INITIAL_FORM_VALUES),
      name: "My QR Code",
      manual,
      withRedirect
    },
    ...defaultValues,
    ...qrCode
  };

  return initialValues;
};

const UPDATE_KEYS = [
  "margin",
  "image",
  "qrOptions",
  "imageOptions",
  "dotsOptions",
  "cornersSquareOptions",
  "cornersDotOptions",
  "backgroundOptions"
];

export const prepareContactlessConfigUpdate = (data) => {
  const updatedData = pick(data, UPDATE_KEYS);
  return {
    data: updatedData
  };
};

export const getCodeDownloadFilename = ({ name, test }) =>
  test ? `[test] ${name}` : name;

export const extendContactlessURLTracking = (url) =>
  extendQS(url, {
    tracking: {
      platform_source: PLATFORM_SOURCE.QR_CODE
    }
  });

export const formatContactlessUrl = ({
  url,
  manual = false,
  withRedirect = false
}) => {
  let result;

  if (manual && withRedirect) {
    result = extendQS(API_PATHS.REDIRECTS, {
      redirect: url
    });
  } else if (manual) {
    result = url;
  } else {
    result = extendContactlessURLTracking(url);
  }
  return result;
};

export const randomizeQRCodeStyles = ({ values, colors }) => {
  const shuffledColors = shuffle(colors);
  const updatedValues = cloneDeep(values);

  set(updatedValues, "dotsOptions.color", shuffledColors[0]);
  set(updatedValues, "dotsOptions.type", sample(DOTS_OPTIONS_TYPES));
  set(updatedValues, "cornersSquareOptions.color", shuffledColors[1]);
  set(
    updatedValues,
    "cornersSquareOptions.type",
    sample(CORNER_SQUARE_OPTIONS_TYPES)
  );
  set(updatedValues, "cornersDotOptions.color", shuffledColors[0]);
  set(
    updatedValues,
    "cornersSquareOptions.type",
    sample(CORNERS_DOT_OPTIONS_TYPES)
  );
  set(updatedValues, "backgroundOptions.color", "#FFFFFF");
  return updatedValues;
};
